import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Store, Plus, Edit, Trash2 } from 'lucide-react';

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeDialog, setStoreDialog] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeForm, setStoreForm] = useState({ 
    name: '', description: '', location: '', store_type: 'general', costing_method: 'weighted_average' 
  });

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/stores');
      setStores(res.data);
    } catch (err) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleSaveStore = async () => {
    try {
      if (selectedStore) {
        await api.put(`/stores/${selectedStore.id}`, storeForm);
        toast.success('Store updated');
      } else {
        await api.post('/stores', storeForm);
        toast.success('Store created');
      }
      setStoreDialog(false);
      resetForm();
      fetchStores();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save store');
    }
  };

  const resetForm = () => {
    setSelectedStore(null);
    setStoreForm({ name: '', description: '', location: '', store_type: 'general', costing_method: 'weighted_average' });
  };

  const openEditDialog = (store) => {
    setSelectedStore(store);
    setStoreForm({
      name: store.name,
      description: store.description || '',
      location: store.location || '',
      store_type: store.store_type || 'general',
      costing_method: store.costing_method || 'weighted_average'
    });
    setStoreDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="stores-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stores / Warehouses</h1>
          <p className="text-gray-500">Manage storage locations and warehouses</p>
        </div>
        <Button onClick={() => { resetForm(); setStoreDialog(true); }} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />Add Store
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Costing Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map(store => (
                <TableRow key={store.id}>
                  <TableCell className="font-mono text-sm">{store.code}</TableCell>
                  <TableCell className="font-medium">{store.name}</TableCell>
                  <TableCell><Badge variant="outline">{store.store_type}</Badge></TableCell>
                  <TableCell>{store.location || '-'}</TableCell>
                  <TableCell className="capitalize">{store.costing_method?.replace('_', ' ')}</TableCell>
                  <TableCell>
                    <Badge className={store.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {store.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(store)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {stores.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Store className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No stores found. Add your first store to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store Dialog */}
      <Dialog open={storeDialog} onOpenChange={setStoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedStore ? 'Edit Store' : 'Add New Store'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Store Name *</Label>
              <Input 
                value={storeForm.name} 
                onChange={(e) => setStoreForm({...storeForm, name: e.target.value})}
                placeholder="Main Warehouse"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea 
                value={storeForm.description}
                onChange={(e) => setStoreForm({...storeForm, description: e.target.value})}
                placeholder="Store description..."
              />
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input 
                value={storeForm.location}
                onChange={(e) => setStoreForm({...storeForm, location: e.target.value})}
                placeholder="Building A, Floor 1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Store Type</Label>
                <Select value={storeForm.store_type} onValueChange={(v) => setStoreForm({...storeForm, store_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                    <SelectItem value="housekeeping">Housekeeping</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Costing Method</Label>
                <Select value={storeForm.costing_method} onValueChange={(v) => setStoreForm({...storeForm, costing_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fifo">FIFO</SelectItem>
                    <SelectItem value="lifo">LIFO</SelectItem>
                    <SelectItem value="weighted_average">Weighted Average</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveStore} className="bg-orange-500 hover:bg-orange-600">
              {selectedStore ? 'Update' : 'Create'} Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
